import React, { FC } from "react";
import { defineMessages, useIntl } from "react-intl";
import Image from "@assets/images/understanding_your_results.png";
import { TestType } from "@app/provider/testData/types";

import "../understandingYourResults.scss";

const messages = defineMessages({
  understandingYourResultsImage: {
    id: "understandingYourResultsImage",
    defaultMessage: "Understanding your results image",
  },
  understandingYourResultsEmpowerFirstParagraph: {
    id: "understandingYourResultsEmpowerFirstParagraph",
    defaultMessage:
      "The Empower Hereditary Cancer Test screens for variants, or mutations, in genes associated with an increased risk for common hereditary cancers. Three results are possible:",
  },
  understandingYourResultsVistaraFirstParagraph: {
    id: "understandingYourResultsVistaraFirstParagraph",
    defaultMessage:
      "Vistara is a non-invasive prenatal test that screens your pregnancy for conditions caused by small changes, or mutations, in individual genes.",
  },
  understandingYourResultsEmpowerFirstCard: {
    id: "understandingYourResultsEmpowerFirstCard",
    defaultMessage:
      "<b>Negative:</b> No variant known to increase risk for cancers was found in the genes tested.",
  },
  understandingYourResultsVistaraFirstCard: {
    id: "understandingYourResultsVistaraFirstCard",
    defaultMessage:
      "Your results will show whether a disease-causing or likely disease-causing single-gene mutation has been detected (“Screen Positive”) or not detected (“Screen Negative”).",
  },
  understandingYourResultsEmpowerSecondCard: {
    id: "understandingYourResultsEmpowerSecondCard",
    defaultMessage:
      "<b>Positive:</b> A variant was found that is associated with an increased risk for certain cancer types.",
  },
  understandingYourResultsEmpowerThirdCard: {
    id: "understandingYourResultsEmpowerThirdCard",
    defaultMessage:
      "<b>Variant of Uncertain Significance (VUS):</b> A variant has been identified, but more research is needed to know if it is associated with an increased risk for cancer. VUS status can change over time.",
  },
});

interface UnderstandingYourResultsDefaultContentProps {
  testType?: TestType;
}

export const UnderstandingYourResultsDefaultContent: FC<
  UnderstandingYourResultsDefaultContentProps
> = ({ testType }) => {
  const intl = useIntl();

  const getFirstParagraphByTestType = () => {
    switch (testType) {
      case TestType.VISTARA:
        return intl.formatMessage(
          messages.understandingYourResultsVistaraFirstParagraph
        );
      default:
        return intl.formatMessage(
          messages.understandingYourResultsEmpowerFirstParagraph
        );
    }
  };

  const getTestCardDescriptionByTestType = () => {
    switch (testType) {
      case TestType.VISTARA:
        return getVistaraDescriptionTestCard();
      default:
        return getEmpowerDescriptionTestCard();
    }
  };

  const getVistaraDescriptionTestCard = () => {
    const cardMessages = [messages.understandingYourResultsVistaraFirstCard];
    return generateTestCardDescription(cardMessages);
  };

  const getEmpowerDescriptionTestCard = () => {
    const cardMessages = [
      messages.understandingYourResultsEmpowerFirstCard,
      messages.understandingYourResultsEmpowerSecondCard,
      messages.understandingYourResultsEmpowerThirdCard,
    ];
    return generateTestCardDescription(cardMessages);
  };

  const generateTestCardDescription = (
    messages: {
      id: string;
      defaultMessage: string;
    }[]
  ) => {
    return (
      <>
        {messages.map((message, index) => (
          <div
            className="understanding-your-results__content__card-wrapper__card"
            key={index}
          >
            {intl.formatMessage(message, { b: (chunks) => <b>{chunks}</b> })}
          </div>
        ))}
      </>
    );
  };

  return (
    <div className="understanding-your-results__content__card-wrapper">
      <p>{getFirstParagraphByTestType()}</p>
      <img
        src={Image}
        alt={intl.formatMessage(messages.understandingYourResultsImage)}
      />
      {getTestCardDescriptionByTestType()}
    </div>
  );
};
